import styles from '../TaskExecution.module.scss';
import TaskParameters from './TaskParameters';
import {
  FormDefinitionElement,
  TASKFORM_DEFINITION_TYPES,
  TaskDefinition,
  TaskExecutionType,
} from '../../../api/models/tasks';
import { formatDate } from '../../../components/miscUtils';
import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAssetTypeIcon, getAssetTypeName, useOpenAsset } from '../../../data/assets/helpers';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { selectTemplatesAndCombinedOntology } from '../../../data/templateSlice';
import { FancyButton } from '../../../components/primitives/FancyButton';
import { useApiContext } from '../../../api';
import { addNewAsset } from '../../../data/assets/queriedAssetsSlice';
import { TpAssetInfo } from '../../../data/assets/interfaces';
import TaskInputAssets from './TaskInputAssets';
import { promptConfirmationBox } from '../../../components/message-boxes/ConfirmBox';

interface TaskOverviewProps {
  selectedTask: TaskExecutionType;
  taskDefinition: TaskDefinition;
}

const TaskOverview = (props: TaskOverviewProps) => {
  const openAsset = useOpenAsset();
  const api = useApiContext();
  const dispatch = useAppDispatch();
  const { selectedTask, taskDefinition } = props;
  const refAssetGrid = useRef<HTMLDivElement>(null);
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  const [cardWidth, setCardWidth] = useState(220);
  const [showTerminal, setShowTerminal] = useState(false);
  const [isLogAvailable, setIsLogAvailable] = useState(false);
  const [fileContent, setFileContent] = useState('');
  const [assetListElements, setAssetListElements] = useState<FormDefinitionElement[]>([]);
  const [parameterElements, setParameterElements] = useState<FormDefinitionElement[]>([]);

  useEffect(() => {
    if (selectedTask.parentAssetId) {
      api.asset.fetchAsset(selectedTask.parentAssetId).then((assetInfo) => {
        dispatch(addNewAsset({ asset: assetInfo }));
      });
    }
  }, [selectedTask]);

  const toggleTerminal = () => {
    if (selectedTask.logAvailable) {
      setIsLogAvailable(true);
      setShowTerminal(true);
      api.task.getTaskExecutionLogs(selectedTask.taskExecutionId).then((result) => {
        const presignedUrl = result.signedUrl;
        if (presignedUrl) {
          fetch(presignedUrl)
            .then((response) => {
              if (response.ok) return response.text();
              throw new Error('Network response was not ok.');
            })
            .then((text) => setFileContent(text))
            .catch((error) => console.error('There was a problem with the fetch operation:', error));
        } else {
          setFileContent('Error: Unable to load content.');
        }
      });
    }
  };

  useEffect(() => {
    for (const asset of selectedTask.assets) {
      dispatch(addNewAsset({ asset }));
    }
    if (taskDefinition) {
      setAssetListElements(
        taskDefinition.formDefinition.elements.filter(
          (element) => element.type === TASKFORM_DEFINITION_TYPES.ASSET_LIST,
        ),
      );
      setParameterElements(
        taskDefinition.formDefinition.elements.filter(
          (element) => element.type != TASKFORM_DEFINITION_TYPES.ASSET_LIST,
        ),
      );
    }
    setShowTerminal(false);
  }, [selectedTask, taskDefinition]);

  return (
    <>
      <div className={styles.metaDataView}>
        <h2>Details </h2>
        <div>
          {taskDefinition.taskType === 'system' && <div className={styles.taskBadge}>System task</div>}
          {selectedTask.executingParameters?.resume_task_execution_id && (
            <div
              className={styles.taskBadge}
              style={{
                backgroundColor: 'var(--highlightTextBg)',
                color: 'var(--prim3-color-darker1)',
                borderColor: 'var(--prim3-color-darker1)',
              }}
            >
              Resumed task
            </div>
          )}
        </div>
        <div className={styles.metaDataElement}>
          <h3 className={styles.taskExecutionKey}>Task execution name:</h3>
          <div className={styles.taskName}>
            {selectedTask.name || selectedTask.executingParameters?.display_name || taskDefinition.displayName}
          </div>
        </div>
        <br />
        <div className={styles.metaDataProperties}>
          <div className={styles.metaDataElement}>
            <div className={styles.taskExecutionKey}>Status:</div>
            <div className={styles.taskExecutionVal}>{selectedTask.status}</div>
          </div>
          <div className={styles.metaDataElement}>
            <div className={styles.taskExecutionKey}>Start time:</div>
            <div className={styles.taskExecutionVal}>{formatDate(selectedTask.startTime)}</div>
          </div>
          {selectedTask.endTime && (
            <div className={styles.metaDataElement}>
              <div className={styles.taskExecutionKey}>End time:</div>
              <div className={styles.taskExecutionVal}>{formatDate(selectedTask.endTime)}</div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.separator} />
      <div className={styles.metaDataView}>
        <h2>Parameters</h2>
        <TaskParameters selectedTask={selectedTask} parameterDefinitions={parameterElements} />
        {['failed', 'canceled'].includes(selectedTask.status) && taskDefinition.taskType !== 'system' && (
          // add 'completed' status if you also want to resume completed tasks (to debug in wrapper)
          <div className={styles.terminalContainer}>
            <FancyButton
              iconName="fa-play"
              onClick={() => {
                const confirmData = {
                  title: 'Resume task execution?',
                  description:
                    'This will rerun the exact same task unless changes have been made to the celery subprocess or nextflow pipeline <strong>and</strong> the celery worker container has been restarted.',
                  cancelButtonLabel: 'Cancel',
                };

                promptConfirmationBox(confirmData)
                  .then(() => {
                    // Handle confirmation with the task execution ID
                    const resumeTaskExecutionId = selectedTask.taskExecutionId;

                    // Call the API to resume the task execution
                    api.task
                      .resumeTaskExecution(resumeTaskExecutionId)
                      .then((taskExecution) => {
                        dispatch(addNewTaskExecution({ taskExecution }));
                      })
                      .catch((error) => {});
                  })
                  .catch(() => {});
              }}
            >
              Resume task
            </FancyButton>
          </div>
        )}
      </div>
      <div className={styles.separator} />
      {assetListElements.map((elem) => {
        return (
          <div key={`input-assets-${elem.id}`}>
            <div className={styles.metaDataView}>
              <h2>{elem.displayName}</h2>
              <TaskInputAssets selectedTask={selectedTask} inputAssetsKey={elem.id} />
            </div>
            <div className={styles.separator} />
          </div>
        );
      })}
      <div className={styles.metaDataView}>
        {selectedTask.assets && selectedTask.assets.length != 0 && (
          <>
            <h2>Created assets</h2>
            <div className={styles.assetGrid} ref={refAssetGrid}>
              {selectedTask.assets.map((asset: TpAssetInfo, index: React.Key) => (
                <div
                  className={styles.assetCard}
                  key={index}
                  style={{ width: cardWidth }}
                  onClick={() => {
                    openAsset(asset.id);
                  }}
                >
                  <div className={styles.assetCardIcon}>
                    <FontAwesomeIcon icon={['fad', getAssetTypeIcon(ontology.assetTypes[asset.type])]} />
                  </div>
                  <div className={styles.assetCardType}>{getAssetTypeName(ontology.assetTypes[asset.type])}</div>
                  <div className={styles.assetCardNameBig}>{asset.displayName}</div>
                  {<div className={styles.assetCardDescription}>{asset.description}</div>}

                  <div className={styles.assetCardDateLine1}>
                    <div className={styles.assetCardLineIcon}>
                      <FontAwesomeIcon icon={['fas', 'calendar-days']} />
                    </div>
                    {asset.creationDate ? formatDate(asset.creationDate) : ''}
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.optionalSeparator} />
          </>
        )}
      </div>
      <div className={styles.metaDataView}>
        {['completed', 'failed', 'canceled'].includes(selectedTask.status) && (
          <>
            <h2>Logs</h2>
            <div className={styles.terminalContainer}>
              {showTerminal ? (
                isLogAvailable ? (
                  <div className={styles.terminal}>
                    <pre>{fileContent || 'Loading...'}</pre>
                  </div>
                ) : (
                  <div>No log content is available for this task execution</div>
                )
              ) : (
                <FancyButton iconName="fa-rotate-right" onClick={toggleTerminal}>
                  Show logs
                </FancyButton>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default TaskOverview;
